import React, { useCallback, useMemo } from 'react';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdCampaignEventType } from '@/enums';
import { AdCampaignModuleType } from '@/types';
import {
  AdImpressionSource,
  DisplayAdModule,
} from '@/contexts/ads-context-types';
import { DisplayAdBanner } from './display-ad-banner';
import { ExpandableText } from '@/components/expandable-text';
import { ItemListOption } from '@/contexts/item-context-types';
import { LIST_MARGIN_HORIZONTAL, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { Props as ListItemProps } from '@/components/list-item';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { StyleSheet, Text, View } from 'react-native';
import { trackAdAnalytics } from '@/utils/ads';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

interface Props {
  displayAd: DisplayAdModule;
  listData: ItemListOption[];
}

export const DisplayAdDetailComponent: React.FC<Props> = ({
  displayAd,
  listData,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { onViewableAdItemsChanged } = useAdsContext();
  const { selectItemListOption } = useItemDetailContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
    user,
  } = useAuthenticatedUserContext();

  const onListItemSelect = useCallback(
    (item: ItemListOption) => {
      const displayAdVPData =
        displayAd.variantPackEventMap[item.metadata!.variantPackUUID]!;

      selectItemListOption(item, { source: 'display_ad_details' });
      trackAdAnalytics({
        adCampaignUUID: displayAd.campaignUUID,
        adManagerUUID: displayAd.adManagerUUID,
        adModule: AdCampaignModuleType.DisplayAd,
        advertiserUUID: displayAd.advertiserUUID,
        distributorUUID: supplierUUID,
        eventType: AdCampaignEventType.CLICK,
        eventUUID: displayAdVPData.variant_pack_event_data.click_event_id,
        gtin: displayAdVPData.gtin,
        operatorUUID: restaurantUUID,
        source: 'display_ad_details',
        userUUID: user.uuid,
        variantPackUUID: displayAdVPData.variant_pack!.uuid, // Should always be initialized
      });
    },
    [displayAd, selectItemListOption, restaurantUUID, supplierUUID, user],
  );

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    return {
      onSelect: onListItemSelect,
      renderMainContent: (option) => {
        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            onOptionsPress={onListItemSelect}
            option={option}
            showListView={false}
            showSingleOption
            source="display_ad_details"
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: 'display_ad_details',
              });
            }}
          />
        );
      },
    };
  }, [debouncedUpdateQuantity, onListItemSelect]);

  const { headerStyle, listContainerStyle } = useSmallLayoutSizeValue(
    {
      headerStyle: null,
      listContainerStyle: { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
    },
    {
      headerStyle: styles.headerLarge,
      listContainerStyle: styles.listContainerLarge,
    },
  );
  const displayAdContent = displayAd.content;

  return (
    <View style={[styles.base, listContainerStyle]}>
      <OrderableItemList
        listData={listData}
        ListHeaderComponent={
          <View style={headerStyle}>
            <DisplayAdBanner
              displayAdContent={displayAdContent}
              withinDetails
            />
            {displayAdContent.title ? (
              <View style={styles.title}>
                <Text style={[TYPOGRAPHY.pSemiBold]}>
                  {displayAdContent.title}
                </Text>
              </View>
            ) : null}
            {displayAdContent.description ? (
              <View style={styles.description}>
                <ExpandableText
                  numberOfLines={4}
                  textStyle={TYPOGRAPHY.small}
                  trackingData={{
                    name: 'display_ad_details.description',
                    properties: {
                      campaignUUID: displayAd.campaignUUID,
                    },
                  }}
                >
                  {displayAdContent.description}
                </ExpandableText>
              </View>
            ) : null}
          </View>
        }
        listItemProps={listItemProps}
        listItemSeparatorStyle={styles.itemSeparator}
        onViewableItemsChanged={({ viewableItems }) =>
          onViewableAdItemsChanged(
            viewableItems,
            // Use same source for display ad carousel and details
            // to prevent duplicate impressions for same item
            AdImpressionSource.OrderGuide,
          )
        }
        renderListHeaderAsListItem={false}
        showListView
        viewabilityConfig={AD_VIEWABILITY_CONFIG}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
  },
  description: {
    marginBottom: 8,
    marginHorizontal: 24,
  },
  headerLarge: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  itemSeparator: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  // Remove extra list padding in action sheet
  listContainerLarge: {
    marginHorizontal: -LIST_MARGIN_HORIZONTAL,
  },
  title: {
    marginBottom: 8,
    marginHorizontal: 24,
    marginTop: 16,
  },
});
